import { useRef, useState } from "react";
import { usePageTitle } from "@/i18n/LanguageContext";
import { pages } from "../content/pages";
import { useContent, SlotImg, Rich } from "../ui/helpers";
import { PageShell, PageHero, CtaSignOff, useInViewClass } from "../ui/blocks";
import { FilmDialog } from "../home/FilmDialog";

/* Machine records + worker reports + business systems → KSS → overview */
function MergeDiagram({ d }) {
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label={`${d.inputs.join(", ")} → KSS → ${d.output}`}>
      <g fontFamily="IBM Plex Sans, Noto Sans KR, sans-serif" fontSize="13" fill="#0B1F2C">
        {d.inputs.map((t, i) => (
          <g key={t}>
            <rect x="10" y={30 + i * 93} width="150" height="54" rx="12" fill="#EEF8F4" stroke="#0A7D66" strokeOpacity=".35" />
            <text x="30" y={62 + i * 93}>{t}</text>
          </g>
        ))}
      </g>
      <g fill="none" stroke="#0A7D66" strokeWidth="2" className="flowline">
        <path d="M160 57 C 210 57, 200 150, 240 150" /><path d="M160 150 L 240 150" /><path d="M160 243 C 210 243, 200 150, 240 150" /><path d="M308 150 L 350 150" />
      </g>
      <rect x="240" y="116" width="68" height="68" rx="16" fill="#0B1F2C" />
      <text x="274" y="156" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="17" fill="#16E6B2">KSS</text>
      <rect x="350" y="70" width="160" height="160" rx="14" fill="#fff" stroke="#0A7D66" strokeOpacity=".3" />
      <text x="366" y="98" fontFamily="IBM Plex Sans, Noto Sans KR, sans-serif" fontSize="12.5" fontWeight="600" fill="#0B1F2C">{d.output}</text>
      <polyline points="366,160 390,146 412,152 436,128 460,136 492,112" fill="none" stroke="#16E6B2" strokeWidth="3" strokeLinecap="round" />
      <rect x="366" y="180" width="110" height="6" rx="3" fill="#16E6B2" /><rect x="366" y="194" width="80" height="6" rx="3" fill="#1EC8FF" /><rect x="366" y="208" width="96" height="6" rx="3" fill="#CFE6DC" />
    </svg>
  );
}

function PrincipleRow({ n, title, body }) {
  const ref = useRef(null);
  useInViewClass(ref, 0.6);
  return (
    <div className="prow" ref={ref}>
      <span className="n">{String(n).padStart(2, "0")}</span>
      <div><h3>{title}</h3><p><Rich text={body} /></p></div>
    </div>
  );
}

export default function CompanyPage() {
  usePageTitle("company");
  const c = useContent(pages).company;
  const common = useContent(pages).common;
  const [film, setFilm] = useState(false);

  return (
    <PageShell testId="company-page">
      <PageHero hero={c.hero} />

      <section className="band-light sec" id="purpose" aria-labelledby="purpose-title">
        <div className="k-wrap purpose">
          <div className="rv">
            <p className="k-label">{c.purpose.eyebrow}</p>
            <h2 className="big" id="purpose-title" style={{ marginTop: 18 }}>{c.purpose.title} <span>{c.purpose.lead}</span></h2>
            <p className="k-body"><Rich text={c.purpose.body} /></p>
          </div>
          <figure className="merge rv" style={{ margin: 0 }}>
            <MergeDiagram d={c.purpose.diagram} />
            <figcaption>{c.purpose.diagram.caption}</figcaption>
          </figure>
        </div>
      </section>

      <section className="navy sec" id="principles">
        <div className="k-wrap">
          <div className="sec-head rv">
            <div>
              <p className="k-label">{c.principles.eyebrow}</p>
              <h2 className="h-sec" style={{ marginTop: 16 }}>{c.principles.title}</h2>
            </div>
            <p>{c.principles.intro}</p>
          </div>
          <div className="princ-list">{c.principles.items.map(([t, b], i) => <PrincipleRow key={t} n={i + 1} title={t} body={b} />)}</div>
        </div>
      </section>

      <section className="mint-2 sec" id="direction">
        <div className="k-wrap">
          <div className="sec-head rv"><h2 className="h-sec">{c.direction.title}</h2><p>{c.direction.intro}</p></div>
          <ol className="journey">
            {c.direction.stops.map((s) => (
              <li key={s.title} className={s.now ? "now" : undefined}><p className="k-label">{s.tag}</p><h3>{s.title}</h3><p>{s.body}</p></li>
            ))}
          </ol>
        </div>
      </section>

      <section className="band-light sec" id="film" style={{ paddingTop: 0 }}>
        <div className="k-wrap">
          <button className="filmband rv" type="button" onClick={() => setFilm(true)} aria-describedby="film-note">
            <SlotImg slot={c.film.media} decorative sizes="100vw" />
            <div className="filmband-in">
              <p className="k-label" style={{ color: "var(--mint)" }}>{c.film.tag}</p>
              <h3>{c.film.title}</h3>
              <span className="k-play"><i aria-hidden="true">▶</i>{c.film.play}</span>
            </div>
          </button>
          <p className="film-note" id="film-note">{c.film.note}</p>
        </div>
      </section>

      <CtaSignOff cta={c.cta} />
      <FilmDialog open={film} onClose={() => setFilm(false)} title={common.filmTitle} closeLabel={common.filmClose} />
    </PageShell>
  );
}
